import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	Paper,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Edit, Delete, Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import { API_URL } from '../config/api';
import BRANDING from "../config/branding";

const ManageMessageCategories = () => {
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);

	// Modal states
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState("create"); // create, update, delete
	const [categoryName, setCategoryName] = useState("");
	const [selectedId, setSelectedId] = useState("");
	const [selectedCategory, setSelectedCategory] = useState(null);

	const token = localStorage.getItem("token");

	const fetchCategories = async () => {
		setLoading(true);
		setError(null);
		try {
			const formData = new FormData();
			formData.append("action", "read");

			const response = await fetch(`${API_URL}/Messages.php`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
				},
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			if (data.status && Array.isArray(data.data)) {
				setCategories(data.data);
			} else {
				toast.error(data.message || "Failed to fetch categories.");
			}
		} catch (err) {
			console.error("Error fetching categories:", err);
			setError(`Failed to load categories: ${err.message}`);
		} finally {
			setLoading(false);
		}
	};

	// Check if category name already exists (case-insensitive)
	const categoryExists = (name, excludeId = null) => {
		return categories.some(
			(cat) =>
				cat.category_name.toLowerCase() === name.toLowerCase() &&
				cat.id !== excludeId
		);
	};

	const handleSubmit = async () => {
		// Validation
		if (modalMode !== "delete" && !categoryName.trim()) {
			toast.error("Category name is required");
			return;
		}

		if (modalMode === "create" && categoryExists(categoryName.trim())) {
			toast.error("A category with this name already exists");
			return;
		}

		if (
			modalMode === "update" &&
			categoryExists(categoryName.trim(), selectedId)
		) {
			toast.error("A category with this name already exists");
			return;
		}

		if ((modalMode === "update" || modalMode === "delete") && !selectedId) {
			toast.error("Please select a category");
			return;
		}

		setSubmitting(true);
		const formData = new FormData();
		const endpoint = `${API_URL}/Messages.php`;

		switch (modalMode) {
			case "create":
				formData.append("action", "create");
				formData.append("category_name", categoryName.trim());
				break;
			case "update":
				formData.append("action", "update");
				formData.append("category_name", categoryName.trim());
				formData.append("id", selectedId);
				break;
			case "delete":
				formData.append("action", "delete");
				formData.append("id", selectedId);
				break;
		}

		try {
			const response = await fetch(endpoint, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
				},
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			if (data.status) {
				toast.success(data.message || "Operation successful");
				await fetchCategories();
				handleCloseModal();
			} else {
				toast.error(data.message || "Operation failed");
			}
		} catch (error) {
			console.error("Error:", error);
			toast.error("An error occurred during the operation");
		} finally {
			setSubmitting(false);
		}
	};

	const handleOpenModal = (mode, category = null) => {
		setModalMode(mode);
		setSelectedCategory(category);
		setSelectedId(category?.id || "");
		setCategoryName(category?.category_name || "");
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setCategoryName("");
		setSelectedId("");
		setSelectedCategory(null);
	};

	// DataGrid columns
	const columns = [
		{ field: "id", headerName: "ID", width: 80 },
		{
			field: "category_name",
			headerName: "Category Name",
			flex: 1,
			minWidth: 200,
		},
		{
			field: "actions",
			headerName: "Actions",
			width: 150,
			sortable: false,
			renderCell: (params) => (
				<Box sx={{ display: "flex", gap: 1 }}>
					<IconButton
						size='small'
						color='primary'
						onClick={() => handleOpenModal("update", params.row)}
						sx={{
							"&:hover": { backgroundColor: "rgba(25, 118, 210, 0.04)" },
						}}
					>
						<Edit fontSize='small' />
					</IconButton>
					<IconButton
						size='small'
						color='error'
						onClick={() => handleOpenModal("delete", params.row)}
						sx={{
							"&:hover": { backgroundColor: "rgba(211, 47, 47, 0.04)" },
						}}
					>
						<Delete fontSize='small' />
					</IconButton>
				</Box>
			),
		},
	];

	useEffect(() => {
		fetchCategories();
	}, []);

	const getModalTitle = () => {
		switch (modalMode) {
			case "create":
				return "Create New Category";
			case "update":
				return "Update Category";
			case "delete":
				return "Delete Category";
			default:
				return "Manage Category";
		}
	};

	return (
		<Box className='p-6 bg-white rounded-md shadow-sm'>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 4,
					pb: 2,
					borderBottom: `2px solid ${BRANDING.primaryColor}20`,
				}}
			>
				  <Typography
        variant='h4'
        sx={{
            fontWeight: 700,
            color: BRANDING.primaryColor,
        }}
    >
        Communication Categories
    </Typography>

    <Typography
        variant='body2'
        sx={{
            color: "#64748B",
            mt: 0.5,
        }}
    >
        Manage message classification and communication groups
    </Typography>
				<Button
					variant='contained'
					startIcon={<Add />}
					onClick={() => handleOpenModal("create")}
					sx={{
						backgroundColor: BRANDING.primaryColor,
						"&:hover": { backgroundColor: BRANDING.primaryColorDark },
						textTransform: "none",
						borderRadius: "10px",
							px: 3,
							fontWeight: 600,
					}}
				>
					Add Category
				</Button>
			</Box>

			{/* Loading State */}
			{loading && (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress sx={{ color: BRANDING.primaryColor }} />
					<Typography variant='body1' sx={{ ml: 2, mt: 0.5 }}>
						Loading categories...
					</Typography>
				</Box>
			)}

			{/* Error State */}
			{error && (
				<Alert
    severity='warning'
    sx={{
        mb: 3,
        borderRadius: 2,
        backgroundColor: "#FFF7ED",
        color: "#F97316",
        border: "1px solid #F97316",

        "& .MuiAlert-icon": {
            color: "#F97316",
        },
    }}
>
					{error}
				</Alert>
			)}

			{/* Categories Table */}
			{!loading && !error && (
				<>
					{categories.length === 0 ? (
						<Paper
							 sx={{
								p: 6,
								textAlign: "center",
								borderRadius: 3,
								border: "1px dashed #CBD5E1",
								backgroundColor: "#F8FAFC",
							}}
						>
							<Typography
								variant="h6"
								sx={{
									color: BRANDING.primaryColor,
									fontWeight: 600,
									mb: 1,
								}}
							>
								No Communication Categories Found
							</Typography>

							<Typography color="text.secondary">
								Create your first category to organize outbound communications.
							</Typography>
						</Paper>
					) : (
						<Paper
							sx={{
								height: 400,
								width: "100%",
								boxShadow: "none",
								border: "1px solid #e0e0e0",
								borderRadius: "8px",
							}}
						>
							<DataGrid
							
							
								rows={categories}
								columns={columns}
								pageSizeOptions={[5, 10, 25]}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 10, page: 0 },
									},
								}}
								disableRowSelectionOnClick
								sx={{
	"& .MuiDataGrid-columnHeaders": {
		backgroundColor: "#FEF2F2",
		fontWeight: 700,
	},
	"& .MuiDataGrid-cell:focus": {
		outline: "none",
	},
}}
							/>
						</Paper>
					)}
				</>
			)}

			{/* Modal for Create/Update/Delete */}
			<Dialog
	
				open={modalOpen}
				onClose={handleCloseModal}
				maxWidth='sm'
				fullWidth
				sx={{ "& .MuiDialog-paper": { borderRadius: "12px" } }}
			>
				<DialogTitle sx={{
        backgroundColor: "#FEF2F2",
        color: BRANDING.primaryColor,
        fontWeight: 700,
    }}
				>
					{getModalTitle()}
					<IconButton onClick={handleCloseModal} sx={{ p: 0.5 }}>
						<Close />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ pt: 2 }}>
					{modalMode === "delete" ? (
						<Box>
							<Typography variant='body1' sx={{ mb: 2 }}>
								Are you sure you want to delete this category?
							</Typography>
							<Paper
								sx={{
									p: 2,
									backgroundColor: "#fafafa",
									border: "1px solid #e0e0e0",
								}}
							>
								<Typography variant='body2' color='textSecondary'>
									Category Name:
								</Typography>
								<Typography variant='body1' fontWeight='medium'>
									{selectedCategory?.category_name}
								</Typography>
							</Paper>
						</Box>
					) : (
						<TextField
							label='Category Name'
							placeholder='Enter category name'
							variant='outlined'
							fullWidth
							value={categoryName}
							onChange={(e) => setCategoryName(e.target.value)}
							error={
								categoryName.trim() &&
								categoryExists(
									categoryName.trim(),
									modalMode === "update" ? selectedId : null
								)
							}
							helperText={
								categoryName.trim() &&
								categoryExists(
									categoryName.trim(),
									modalMode === "update" ? selectedId : null
								)
									? "A category with this name already exists"
									: ""
							}
							sx={{
	mb: 2,
	"& .MuiOutlinedInput-root": {
		"&.Mui-focused fieldset": {
			borderColor: BRANDING.primaryColor,
		},
	},
	"& .MuiInputLabel-root.Mui-focused": {
		color: BRANDING.primaryColor,
	},
}}
						/>
					)}
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button
						onClick={handleCloseModal}
						variant='outlined'
						sx={{
							textTransform: "none",
							borderRadius: "8px",
							borderColor: "#ccc",
							color: "#555",
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						variant='contained'
						disabled={
							submitting ||
							(modalMode !== "delete" && !categoryName.trim()) ||
							(modalMode !== "delete" &&
								categoryName.trim() &&
								categoryExists(
									categoryName.trim(),
									modalMode === "update" ? selectedId : null
								))
						}
						sx={{
							backgroundColor: modalMode === "delete" ?  "#d32f2f"
	: BRANDING.primaryColor,
							"&:hover": {
								backgroundColor: modalMode === "delete" ? "#b71c1c" : "#B91C1C",
							},
							textTransform: "none",
							borderRadius: "8px",
						}}
					>
						{submitting ? (
							<CircularProgress size={20} color='inherit' />
						) : (
							{
								create: "Create Category",
								update: "Update Category",
								delete: "Delete Category",
							}[modalMode]
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ManageMessageCategories;
